import { useState } from "react";
import { Wallet, X, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const MIN = 5;
const MAX = 200;
const PRESETS = [10, 25, 50, 100];

// "Add funds" — face-value cash top-up: pay $X, get exactly $X of balance.
export const BuyCreditsModal = ({ open, onClose, initialAmount = 25, headline }) => {
  const [amount, setAmount] = useState(initialAmount);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const price = Number(amount).toFixed(2);

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-custom", {
        amount_usd: Number(amount),
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("no-url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="buy-credits-modal"
    >
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border border-[#dfe7d8] my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative px-6 py-5 text-white" style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>
          <button data-testid="buy-credits-close" onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15 transition-colors"><X size={20} /></button>
          <div className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Wallet size={22} /></span>
            <div>
              <h3 className="text-lg font-extrabold leading-tight">Add Funds</h3>
              <p className="text-[13px] font-semibold text-white/85 leading-tight">{headline || "Add cash to your wallet — spend it whenever"}</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <div className="text-center">
            <div className="inline-flex items-baseline gap-1">
              <span className="text-5xl font-extrabold text-[#171426] tabular-nums leading-none" data-testid="buy-credits-amount">${price}</span>
              <span className="text-sm text-[#9a96ac] font-semibold">USD</span>
            </div>
            <div className="text-[12px] text-[#9a96ac] mt-2">Face value — you get exactly what you add · never expires</div>
          </div>

          <input
            type="range"
            min={MIN}
            max={MAX}
            step={5}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            data-testid="buy-credits-slider"
            className="w-full mt-5 accent-[#5d8a52] cursor-pointer"
          />
          <div className="flex justify-between text-[11px] font-semibold text-[#b3aec6] mt-1">
            <span>${MIN}</span><span>${MAX}</span>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {PRESETS.map((p) => (
              <button
                key={p}
                data-testid={`buy-credits-preset-${p}`}
                onClick={() => setAmount(p)}
                className={`flex items-center gap-1 text-sm font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${amount === p ? "bg-[#eef3ea] border-[#5d8a52] text-[#3f6238]" : "border-[#e3e0ee] text-[#6b6880] hover:border-[#a9c39c]"}`}
              >
                {amount === p && <Check size={13} />}${p}
              </button>
            ))}
          </div>

          <button
            data-testid="buy-credits-checkout"
            onClick={checkout}
            disabled={busy}
            className="w-full mt-6 inline-flex items-center justify-center gap-2 font-extrabold text-white py-3 rounded-2xl transition-colors disabled:opacity-70"
            style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}
          >
            {busy ? <><Loader2 size={18} className="spin" /> Starting checkout…</> : <><Wallet size={18} /> Add ${price} to wallet</>}
          </button>
          <p className="text-center text-[11px] text-[#b3aec6] mt-3">Secure one-time checkout by Stripe. Funds are added instantly.</p>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsModal;
